// Office Phone Line Form Logic
// Handles form field dependencies and business rules
import { get } from 'lodash';
import { planAddOnsConfig } from './config';

// Pricing data based on the requirements
export const pricingData = {
  'Forborne': {
    '36 Months': {
      'Office Phone Basic': { price: 25 },
      'Office Phone': { price: 35 },
      'Office Phone Plus': { price: 50, hasCustomPricing: true }
    },
    'Month to Month': {
      'Office Phone Basic': { price: 30 },
      'Office Phone': { price: 40 },
      'Office Phone Plus': { price: 50 } // No pricing available
    }
  },
  'Non-Forborne / Tariffed': {
    'Month to Month': {
      'Office Phone Basic': { price: 66 },
      'Office Phone': { price: 75 },
      'Office Phone Plus': { price: 85 }
    }
  }
};

// Custom pricing options for Office Phone Plus (36 months)
export const customPricingOptions = [
  { value: '45', label: '45' },
  { value: '50', label: '50' },
  { value: '60', label: '60' },
  { value: '70', label: '70' },
  { value: '80', label: '80' },
  { value: '90', label: '90' }
];

// Get available terms based on order type
export const getAvailableTerms = (orderType) => {
  if (!orderType) return [];
  
  if (orderType === 'Forborne') {
    return [
      { value: '36 Months', label: '36 Months' },
      { value: 'Month to Month', label: 'Month to Month' }
    ];
  } else if (orderType === 'Non-Forborne / Tariffed') {
    return [
      { value: 'Month to Month', label: 'Month to Month' }
    ];
  }
  
  return [];
};

// Get available plans based on order type and term
export const getAvailablePlans = (orderType, term) => {
  if (!orderType || !term) return [];
  
  const plans = [
    { value: 'Office Phone Basic', label: 'Office Phone Basic' },
    { value: 'Office Phone', label: 'Office Phone' },
    { value: 'Office Phone Plus', label: 'Office Phone Plus' }
  ];
  
  // Filter out plans that don't have pricing for the selected combination
  return plans.filter(plan => {
    const pricing = get(pricingData, [orderType, term, plan.value], null);
    return pricing && pricing.price !== null;
  });
};

// Check if custom pricing field should be shown
export const shouldShowCustomPricing = (orderType, term, plan) => {
  if (!orderType || !term || !plan) return false;
  
  // Handle both string values and objects with .value property
  const planValue = typeof plan === 'string' ? plan : plan.value;
  const pricing = get(pricingData, [orderType, term, planValue], null);
  return pricing && pricing.hasCustomPricing === true;
};

// Get the base price for a plan
export const getBasePrice = (orderType, term, plan) => {
  if (!orderType || !term || !plan) return null;
  
  const pricing = get(pricingData, [orderType, term, plan.value], null);
  return pricing ? pricing.price : null;
};

// Form field dependency logic
export const handleFieldChange = (fieldName, value, currentValues, setFieldValue) => {
  switch (fieldName) {
    case 'orderType':
      // Clear dependent fields when order type changes
      setFieldValue('term', '');
      setFieldValue('plan', '');
      setFieldValue('customPrice', '');
      break;
      
    case 'term':
      // Clear dependent fields when term changes
      setFieldValue('plan', '');
      setFieldValue('customPrice', '');
      break;
      
    case 'plan':
      // Clear custom price and calling features when plan changes
      setFieldValue('customPrice', '');
      setFieldValue('oneFeature', '');
      setFieldValue('unlimited', '');
      // Set included add-ons for the new plan
      const includedAddOns = getIncludedAddOns(value);
      setFieldValue('addOns', includedAddOns);
      break;
      
    case 'lineDesignation':
      // Clear line type when line designation changes
      setFieldValue('lineType', '');
      setFieldValue('numberOfLines', '');
      setFieldValue('phoneNumbers', '');
      break;
      
    case 'lineType':
      // Clear number of lines and phone numbers when line type changes
      setFieldValue('numberOfLines', '');
      setFieldValue('phoneNumbers', '');
      break;
      
    case 'customerType':
      // Clear phone numbers when customer type changes
      setFieldValue('phoneNumbers', '');
      break;
      
    default:
      break;
  }
};

// Validate form based on business rules
export const validateForm = (values) => {
  const errors = {};
  
  // Required field validation
  if (!values.orderType) {
    errors.orderType = 'Order Type is required';
  }
  
  if (!values.term) {
    errors.term = 'Term is required';
  }
  
  if (!values.plan) {
    errors.plan = 'Plan is required';
  }
  
  if (!values.customerType) {
    errors.customerType = 'Customer Type is required';
  }
  
  if (!values.lineDesignation) {
    errors.lineDesignation = 'Line Designation is required';
  }
  
  if (!values.lineType) {
    errors.lineType = 'Line Type is required';
  }
  
  if (!values.tollFreeNumber) {
    errors.tollFreeNumber = 'Toll Free Number is required';
  }
  
  // Custom pricing validation
  if (shouldShowCustomPricing(values.orderType, values.term, values.plan) && !values.customPrice) {
    errors.customPrice = 'Custom price is required for this plan';
  }
  
  // Number of Lines validation (only when field is visible)
  if (shouldShowNumberOfLines(values.lineType)) {
    if (!values.numberOfLines) {
      errors.numberOfLines = 'Number of lines is required';
    } else {
      const numLines = parseInt(values.numberOfLines);
      if (isNaN(numLines) || numLines < 1 || numLines > 10) {
        errors.numberOfLines = 'A maximum of 10 lines can be added.';
      }
    }
  }
  
  return errors;
};

// Get available line types based on line designation
export const getAvailableLineTypes = (lineDesignation) => {
  const allLineTypes = [
    { value: 'Single Line', label: 'Single Line' },
    { value: 'Multi Line', label: 'Multi Line' }
  ];
  
  // Only show Multi Line option if Line Designation = Phone
  if (lineDesignation === 'Phone') {
    return allLineTypes;
  } else {
    // Return only Single Line for other designations
    return allLineTypes.filter(type => type.value === 'Single Line');
  }
};

// Check if Number of Lines field should be shown
export const shouldShowNumberOfLines = (lineType) => {
  return lineType === 'Multi Line';
};

// Check if Phone Number field should be shown (Winback or Existing Customers)
export const shouldShowPhoneNumber = (customerType) => {
  return customerType === 'Winback' || customerType === 'Existing Customers';
};

// Check if 1 Feature field should be shown (only for Office Phone Basic)
export const shouldShowOneFeature = (plan) => {
  const planValue = typeof plan === 'string' ? plan : plan.value;
  return planValue === 'Office Phone Basic';
};

// Check if Unlimited field should be shown (for Office Phone and Office Phone Plus)
export const shouldShowUnlimited = (plan) => {
  const planValue = typeof plan === 'string' ? plan : plan.value;
  return planValue === 'Office Phone' || planValue === 'Office Phone Plus';
};

// Get available add-ons for a specific plan
export const getAvailableAddOns = (plan) => {
  const planValue = typeof plan === 'string' ? plan : plan.value;
  const config = get(planAddOnsConfig, planValue, { available: [], included: [] });
  return config.available;
};

// Get included add-ons for a specific plan
export const getIncludedAddOns = (plan) => {
  const planValue = typeof plan === 'string' ? plan : plan.value;
  const config = get(planAddOnsConfig, planValue, { available: [], included: [] });
  return config.included;
};

